import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import Lenis from 'lenis';
import { Activity, ArrowUp, ArrowUpRight, Menu, X } from 'lucide-react';
import { Toaster } from 'sonner';

const nav = [
  ['Why Simulation', '/#why-simulation'],
  ['Simulation Solutions', '/#solutions'],
  ['Medical Equipment', '/medical-equipment-solutions'],
  ['Who We Serve', '/#who-we-serve'],
  ['Our Approach', '/#approach'],
  ['CSR Partnerships', '/csr-partnerships'],
  ['About', '/#about'],
  ['Contact', '/#contact'],
];

const slug = (label) => label.toLowerCase().replaceAll(' ', '-');

function ScrollManager() {
  const { pathname, hash, key } = useLocation();
  useEffect(() => {
    if (!hash) {
      const timer = window.setTimeout(() => {
        if (window.__lenis) window.__lenis.scrollTo(0, { immediate: true });
        else window.scrollTo(0, 0);
      }, 60);
      return () => window.clearTimeout(timer);
    }
    const scrollToTarget = () => {
      const el = document.querySelector(hash);
      if (!el) return;
      if (window.__lenis) window.__lenis.scrollTo(el, { offset: -96 });
      else el.scrollIntoView();
    };
    const timers = [80, 600, 1300].map((delay) => window.setTimeout(scrollToTarget, delay));
    return () => timers.forEach((t) => window.clearTimeout(t));
  }, [pathname, hash, key]);

  useEffect(() => {
    const onClick = (e) => {
      const anchor = e.target.closest('a[href*="#"]');
      if (!anchor) return;
      const url = new URL(anchor.href, window.location.origin);
      if (url.pathname === window.location.pathname && url.hash) {
        const el = document.querySelector(url.hash);
        if (el) {
          e.preventDefault();
          if (window.__lenis) window.__lenis.scrollTo(el, { offset: -96 });
          else el.scrollIntoView({ behavior: 'smooth' });
        }
      }
    };
    document.addEventListener('click', onClick);
    return () => document.removeEventListener('click', onClick);
  }, []);
  return null;
}

export default function Layout({ children }) {
  const [menuOpen, setMenuOpen] = useState(false);
  const [showTop, setShowTop] = useState(false);
  const { pathname } = useLocation();

  useEffect(() => { setMenuOpen(false); }, [pathname]);

  useEffect(() => {
    const onScroll = () => setShowTop(window.scrollY > 500);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    const lenis = new Lenis({ lerp: 0.1 });
    window.__lenis = lenis;
    let rafId;
    const loop = (time) => { lenis.raf(time); rafId = requestAnimationFrame(loop); };
    rafId = requestAnimationFrame(loop);
    return () => { cancelAnimationFrame(rafId); lenis.destroy(); window.__lenis = null; };
  }, []);

  return <div className="site-shell">
    <Toaster position="top-right" />
    <ScrollManager />
    <div className="notice-bar" data-testid="advisory-notice">Independent healthcare technology consultancy · <a data-testid="notice-email-link" href="mailto:info@healthconsul.in">info@healthconsul.in</a></div>
    <header className="site-header">
      <Link to="/" className="brand" data-testid="brand-link"><span className="brand-mark"><Activity size={20} /></span><span><strong>HEALTH<span>CONSULT</span></strong><small>Medical simulation advisory</small></span></Link>
      <nav className="desktop-nav" aria-label="Main navigation">{nav.map(([label, href]) => <Link key={href} to={href} data-testid={`nav-${slug(label)}`}>{label}</Link>)}</nav>
      <Link className="header-cta" to="/#contact" data-testid="header-contact-link">Discuss a project <ArrowUpRight size={16} /></Link>
      <button className="menu-button" onClick={() => setMenuOpen(!menuOpen)} aria-label="Toggle navigation" data-testid="mobile-menu-button">{menuOpen ? <X /> : <Menu />}</button>
      {menuOpen && <nav className="mobile-nav" data-testid="mobile-navigation">{nav.map(([label, href]) => <Link key={href} to={href} data-testid={`mobile-nav-${slug(label)}`}>{label}</Link>)}</nav>}
    </header>
    {children}
    <button
      className={`back-to-top ${showTop ? 'back-to-top-visible' : ''}`}
      onClick={() => { if (window.__lenis) window.__lenis.scrollTo(0); else window.scrollTo(0, 0); }}
      aria-label="Back to top"
      data-testid="back-to-top-button"
    ><ArrowUp size={18} /></button>
    <footer className="site-footer">
      <div>
        <Link className="brand brand-footer" to="/" data-testid="footer-brand-link"><span className="brand-mark"><Activity size={20} /></span><span><strong>HEALTH<span>CONSULT</span></strong><small>Medical simulation advisory</small></span></Link>
        <p>Medical Simulation <i>•</i> Clinical Skills <i>•</i> Healthcare Education</p>
      </div>
      <div className="footer-contact">
        <span>ENQUIRE</span>
        <a href="mailto:info@healthconsul.in" data-testid="footer-email-link">info@healthconsul.in</a>
        <Link to="/csr-partnerships" data-testid="footer-csr-link">CSR Partnerships</Link>
        <small>healthconsul.in</small>
      </div>
      <div className="copyright">© {new Date().getFullYear()} HealthConsult. All rights reserved.</div>
    </footer>
  </div>;
}
